import React, { useRef } from 'react';

/**
 * Professional date input for mobile.
 *
 * Strategy: hidden native date input + visible styled button.
 * The native input is 0x0 and invisible — only used to trigger
 * the OS date picker. The button shows the value or placeholder.
 *
 * This avoids ALL browser inconsistencies with date input styling.
 */
function DateInput({ value, onChange, style, placeholder, 'aria-label': ariaLabel }) {
  const ref = useRef(null);

  const { flex, minWidth, width, padding, ...rest } = style || {};

  const openPicker = () => {
    try { ref.current?.showPicker(); }
    catch { ref.current?.focus(); }
  };

  // Format date for display: "2026-03-25" → "Mar 25"
  const displayValue = value ? formatDate(value) : null;

  return (
    <div style={{ position: 'relative', flex, minWidth, width }}>
      {/* Hidden native date input — only for the OS picker */}
      <input
        ref={ref}
        type="date"
        value={value}
        onChange={onChange}
        aria-label={ariaLabel || placeholder || 'Date'}
        className="date-input-hidden"
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          opacity: 0,
          zIndex: 2,
          cursor: 'pointer',
          padding: 0, margin: 0, border: 'none',
        }}
      />
      {/* Visible button — fully controlled styling */}
      <div
        onClick={openPicker}
        style={{
          ...rest,
          padding: padding || '9px 12px',
          width: '100%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          color: displayValue ? '#eee8ff' : 'rgba(220,210,255,0.45)',
          fontSize: '13px',
        }}
      >
        <span style={{ fontSize: '12px', opacity: 0.6, flexShrink: 0 }}>📅</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {displayValue || placeholder || 'Date'}
        </span>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  try {
    const [y, m, d] = dateStr.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(m) - 1]} ${parseInt(d)}`;
  } catch { return dateStr; }
}

export default DateInput;
